/**
 * SearchResultsPage - Story 7.5: Implement Product Search
 * Dedicated product search results page with filters, sorting, and pagination
 *
 * Features:
 * - Full-text search with query parameter
 * - Search results displayed in ProductGrid (reused from Story 7.2)
 * - Filter sidebar (category, location) for narrowing results
 * - Sort options (newest, price ascending/descending)
 * - Pagination support
 * - Highlight search terms in results (optional)
 * - Empty state with helpful message
 * - Mobile responsive with collapsible sidebar
 * - SEO optimized with meta tags
 *
 * This page is PUBLIC and does not require authentication.
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import { ProductGrid } from '../components/ProductGrid';
import { FilterSidebar } from '../components/FilterSidebar';
import { SortDropdown } from '../components/SortDropdown';
import { SearchBar } from '../components/SearchBar';
import { usePublicProductFilters } from '../hooks/usePublicProductFilters';
import { fetchProductsWithFilters } from '../services/marketplacePublicService';
import { Button } from '@/components/ui/button';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import { Menu } from 'lucide-react';
import type { ProductBrowsePageProps, LocationOption } from '../types/publicMarketplace.types';

// ============================================================================
// Constants
// ============================================================================

/**
 * Page metadata template
 */
const getPageMetadata = (searchQuery: string) => ({
  title: searchQuery
    ? `Search Results for "${searchQuery}" - SAUBHAGYA Marketplace`
    : 'Product Search - SAUBHAGYA Marketplace',
  description: searchQuery
    ? `Search results for "${searchQuery}". Browse biogas products, equipment, seeds, and more from verified sellers.`
    : 'Search and browse biogas products, equipment, seeds, fertilizers, and biomass from verified sellers across India.',
  keywords: searchQuery
    ? `${searchQuery}, biogas products, marketplace, equipment, seeds, fertilizer`
    : 'biogas marketplace, product search, biogas equipment, seeds, fertilizer, biomass',
});

/**
 * Products per page for pagination
 */
const PAGE_SIZE = 20;

/**
 * Indian states and districts (fallback data)
 */
const STATES_DISTRICTS: Record<string, string[]> = {
  'Andhra Pradesh': ['Visakhapatnam', 'Krishna', 'Guntur', 'Nellore', 'Prakasam'],
  'Bihar': ['Patna', 'East Champaran', 'West Champaran', 'Bhagalpur', 'Madhubani'],
  'Gujarat': ['Ahmedabad', 'Surat', 'Vadodara', 'Rajkot', 'Junagadh'],
  'Haryana': ['Faridabad', 'Gurgaon', 'Hisar', 'Rohtak', 'Panipat'],
  'Jharkhand': ['Ranchi', 'Dhanbad', 'Giridih', 'Deoghar', 'East Singhbhum'],
  'Karnataka': ['Bangalore', 'Mysore', 'Belgaum', 'Mangalore', 'Tumkur'],
  'Kerala': ['Kochi', 'Thiruvananthapuram', 'Kozhikode', 'Thrissur', 'Ernakulam'],
  'Madhya Pradesh': ['Indore', 'Bhopal', 'Jabalpur', 'Gwalior', 'Ujjain'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Aurangabad', 'Nashik'],
  'Odisha': ['Bhubaneswar', 'Cuttack', 'Rourkela', 'Sambalpur', 'Berhampur'],
  'Punjab': ['Ludhiana', 'Amritsar', 'Jalandhar', 'Patiala', 'Bathinda'],
  'Rajasthan': ['Jaipur', 'Jodhpur', 'Kota', 'Ajmer', 'Bikaner'],
  'Tamil Nadu': ['Chennai', 'Coimbatore', 'Madurai', 'Salem', 'Tiruchirappalli'],
  'Telangana': ['Hyderabad', 'Warangal', 'Nizamabad', 'Khammam', 'Karimnagar'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Agra', 'Varanasi', 'Meerut'],
  'West Bengal': ['Kolkata', 'Howrah', 'Durgapur', 'Siliguri', 'Asansol'],
};

// ============================================================================
// Component
// ============================================================================

/**
 * SearchResultsPage Component
 * Dedicated page for displaying product search results
 */
export const SearchResultsPage: React.FC<ProductBrowsePageProps> = ({
  className,
  onProductClick: onProductClickProp,
}) => {
  // ============================================================================
  // Hooks
  // ============================================================================

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { filters, setSearch, setCategory, setState, setDistrict, setSortBy, goToPage, resetFilters } =
    usePublicProductFilters();

  // ============================================================================
  // State
  // ============================================================================

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // ============================================================================
  // Derived State
  // ============================================================================

  /**
   * Get initial search query from URL params
   */
  const initialSearchQuery = useMemo(() => {
    return searchParams.get('q') || '';
  }, [searchParams]);

  /**
   * Metadata for SEO
   */
  const pageMetadata = useMemo(
    () => getPageMetadata(initialSearchQuery),
    [initialSearchQuery]
  );

  /**
   * Get states list for filter sidebar
   */
  const states: LocationOption[] = useMemo(
    () =>
      Object.keys(STATES_DISTRICTS).map((state) => ({
        value: state,
        label: state,
      })),
    []
  );

  /**
   * Get districts for selected state
   */
  const districts: LocationOption[] = useMemo(
    () => {
      if (!filters.state) return [];
      const districtNames = STATES_DISTRICTS[filters.state] || [];
      return districtNames.map((district) => ({
        value: district,
        label: district,
      }));
    },
    [filters.state]
  );

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Sync URL search query to filter state
   */
  useEffect(() => {
    const queryParam = searchParams.get('q');
    if (queryParam && queryParam !== filters.search) {
      setSearch(queryParam);
    }
  }, [searchParams, filters.search, setSearch]);

  /**
   * Sync filters to URL
   */
  useEffect(() => {
    const newParams = new URLSearchParams();

    if (filters.search) {
      newParams.set('q', filters.search);
    }
    if (filters.category) {
      newParams.set('category', filters.category);
    }
    if (filters.state) {
      newParams.set('state', filters.state);
    }
    if (filters.district) {
      newParams.set('district', filters.district);
    }
    if (filters.sortBy !== 'newest') {
      newParams.set('sort', filters.sortBy);
    }
    if (filters.page !== 0) {
      newParams.set('page', filters.page.toString());
    }

    const newUrl = newParams.toString()
      ? `/public/marketplace/search?${newParams.toString()}`
      : '/public/marketplace/search';

    navigate(newUrl, { replace: true });
  }, [filters, navigate]);

  // ============================================================================
  // Data Fetching
  // ============================================================================

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: [
      'searchProducts',
      filters.search,
      filters.category,
      filters.state,
      filters.district,
      filters.sortBy,
      filters.page,
    ],
    queryFn: () =>
      fetchProductsWithFilters({
        search: filters.search,
        category: filters.category,
        state: filters.state,
        district: filters.district,
        sortBy: filters.sortBy,
        page: filters.page,
        pageSize: PAGE_SIZE,
      }),
    enabled: !!filters.search, // Only fetch when there's a search query
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
  });

  // ============================================================================
  // Handlers
  // ============================================================================

  /**
   * Handle product card click
   */
  const handleProductClick = useCallback(
    (productId: string) => {
      if (onProductClickProp) {
        onProductClickProp(productId);
      } else {
        navigate(`/public/marketplace/products/${productId}`);
      }
    },
    [navigate, onProductClickProp]
  );

  /**
   * Handle search submission
   */
  const handleSearch = useCallback(
    (query: string) => {
      setSearch(query);
      goToPage(0); // Reset to first page
    },
    [setSearch, goToPage]
  );

  /**
   * Handle category filter change
   */
  const handleCategoryChange = useCallback(
    (category: string | null) => {
      setCategory(category as any);
      goToPage(0);
    },
    [setCategory, goToPage]
  );

  /**
   * Handle state filter change
   */
  const handleStateChange = useCallback(
    (state: string | null) => {
      setState(state);
      setDistrict(null); // Reset district when state changes
      goToPage(0);
    },
    [setState, setDistrict, goToPage]
  );

  /**
   * Handle district filter change
   */
  const handleDistrictChange = useCallback(
    (district: string | null) => {
      setDistrict(district);
      goToPage(0);
    },
    [setDistrict, goToPage]
  );

  /**
   * Handle sort change
   */
  const handleSortChange = useCallback(
    (sortBy: 'newest' | 'price_low' | 'price_high') => {
      setSortBy(sortBy);
      goToPage(0);
    },
    [setSortBy, goToPage]
  );

  /**
   * Handle pagination
   */
  const handlePageChange = useCallback(
    (newPage: number) => {
      goToPage(newPage);
      // Scroll to top of results
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [goToPage]
  );

  /**
   * Handle reset filters
   */
  const handleResetFilters = useCallback(() => {
    resetFilters();
    setSearch(initialSearchQuery);
  }, [resetFilters, setSearch, initialSearchQuery]);

  // ============================================================================
  // Render
  // ============================================================================

  const hasActiveFilters = filters.category || filters.state || filters.district || filters.sortBy !== 'newest';
  const totalPages = data?.pagination?.totalPages || 0;
  const currentPage = filters.page;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{pageMetadata.title}</title>
        <meta name="description" content={pageMetadata.description} />
        <meta name="keywords" content={pageMetadata.keywords} />
        <meta property="og:title" content={pageMetadata.title} />
        <meta property="og:description" content={pageMetadata.description} />
      </Helmet>

      {/* Main Container */}
      <div className={cn('min-h-screen bg-gray-50', className)}>
        {/* Header with Search Bar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col gap-4">
              {/* Title and Back Button */}
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  {initialSearchQuery
                    ? `Search Results for "${initialSearchQuery}"`
                    : 'Product Search'}
                </h1>
              </div>

              {/* Search Bar */}
              <div className="w-full max-w-2xl">
                <SearchBar
                  initialValue={filters.search}
                  placeholder="Search products by name, category..."
                  onSearch={handleSearch}
                  debounceDelay={300}
                  autoFocus={false}
                />
              </div>

              {/* Results Info and Mobile Filter Toggle */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {data ? (
                    <>
                      <span className="font-semibold">{data.pagination.totalItems}</span>
                      {' '}result{data.pagination.totalItems !== 1 ? 's' : ''} found
                    </>
                  ) : (
                    'Loading results...'
                  )}
                </p>

                {/* Mobile Filter Toggle */}
                <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className="md:hidden"
                      aria-label="Toggle filters"
                    >
                      <Menu className="h-4 w-4 mr-2" />
                      Filters
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-80">
                    <SheetHeader>
                      <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterSidebar
                        filters={{
                          search: filters.search,
                          category: filters.category || null,
                          state: filters.state || null,
                          district: filters.district || null,
                          sortBy: filters.sortBy,
                          page: filters.page,
                          pageSize: PAGE_SIZE,
                        }}
                        onFiltersChange={(updates) => {
                          if (updates.category !== undefined) {
                            handleCategoryChange(updates.category as any);
                          }
                          if (updates.state !== undefined) {
                            handleStateChange(updates.state);
                          }
                          if (updates.district !== undefined) {
                            handleDistrictChange(updates.district);
                          }
                        }}
                        states={states}
                        districts={districts}
                        isLoading={isLoading}
                      />
                    </div>
                  </SheetContent>
                </Sheet>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid gap-6 grid-cols-1 md:grid-cols-4">
            {/* Sidebar (Desktop Only) */}
            <div className="hidden md:block">
              <div className="sticky top-24">
                <FilterSidebar
                  filters={{
                    search: filters.search,
                    category: filters.category || null,
                    state: filters.state || null,
                    district: filters.district || null,
                    sortBy: filters.sortBy,
                    page: filters.page,
                    pageSize: PAGE_SIZE,
                  }}
                  onFiltersChange={(updates) => {
                    if (updates.category !== undefined) {
                      handleCategoryChange(updates.category as any);
                    }
                    if (updates.state !== undefined) {
                      handleStateChange(updates.state);
                    }
                    if (updates.district !== undefined) {
                      handleDistrictChange(updates.district);
                    }
                  }}
                  states={states}
                  districts={districts}
                  isLoading={isLoading}
                />
              </div>
            </div>

            {/* Product Grid */}
            <div className="md:col-span-3">
              {/* Sort Bar */}
              <div className="mb-6 flex items-center justify-between gap-3">
                <div className="flex-1" />
                <SortDropdown
                  value={filters.sortBy}
                  onChange={handleSortChange}
                  options={[
                    { value: 'newest', label: 'Newest' },
                    { value: 'price_low', label: 'Price: Low to High' },
                    { value: 'price_high', label: 'Price: High to Low' },
                  ]}
                />
              </div>

              {/* No Search Query State */}
              {!filters.search && !isLoading ? (
                <div className="flex min-h-[500px] items-center justify-center">
                  <div className="text-center max-w-md">
                    <div className="mb-4 text-6xl">🔍</div>
                    <h2 className="mb-2 text-2xl font-semibold text-gray-900">
                      Start Your Search
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Enter a search query above to find products, equipment, seeds, and more from our verified sellers.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Results Grid */}
                  <ProductGrid
                    products={data?.data || []}
                    isLoading={isLoading}
                    isError={isError}
                    error={error as any}
                    onProductClick={handleProductClick}
                    onRetry={refetch}
                  />

                  {/* Pagination */}
                  {data && data.pagination.totalPages > 1 && !isLoading && !isError && (
                    <div className="mt-12 flex justify-center">
                      <Pagination>
                        <PaginationContent>
                          {/* Previous Button */}
                          {data.pagination.hasPrev && (
                            <PaginationItem>
                              <PaginationPrevious
                                onClick={() => handlePageChange(currentPage - 1)}
                                aria-label="Previous page"
                              />
                            </PaginationItem>
                          )}

                          {/* Page Numbers */}
                          {Array.from({ length: totalPages }).map((_, index) => {
                            const pageNum = index;
                            const isCurrentPage = pageNum === currentPage;
                            const isEllipsis =
                              pageNum > currentPage + 1 && pageNum < totalPages - 1;

                            if (isEllipsis) {
                              return (
                                <PaginationItem key={`ellipsis-${pageNum}`}>
                                  <PaginationEllipsis />
                                </PaginationItem>
                              );
                            }

                            if (
                              pageNum === 0 ||
                              pageNum === totalPages - 1 ||
                              Math.abs(pageNum - currentPage) <= 1
                            ) {
                              return (
                                <PaginationItem key={pageNum}>
                                  <PaginationLink
                                    onClick={() => handlePageChange(pageNum)}
                                    isActive={isCurrentPage}
                                    aria-label={`Go to page ${pageNum + 1}`}
                                    aria-current={isCurrentPage ? 'page' : undefined}
                                  >
                                    {pageNum + 1}
                                  </PaginationLink>
                                </PaginationItem>
                              );
                            }

                            return null;
                          })}

                          {/* Next Button */}
                          {data.pagination.hasNext && (
                            <PaginationItem>
                              <PaginationNext
                                onClick={() => handlePageChange(currentPage + 1)}
                                aria-label="Next page"
                              />
                            </PaginationItem>
                          )}
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}

                  {/* Empty Results State */}
                  {data && data.data.length === 0 && !isLoading && !isError && filters.search && (
                    <div className="flex min-h-[500px] items-center justify-center">
                      <div className="text-center max-w-md">
                        <div className="mb-4 text-6xl">📭</div>
                        <h2 className="mb-2 text-2xl font-semibold text-gray-900">
                          No Results Found
                        </h2>
                        <p className="text-gray-600 mb-6">
                          No results for '{filters.search}'. Try different keywords, or adjust your filters.
                        </p>
                        <Button
                          onClick={handleResetFilters}
                          variant="outline"
                        >
                          Clear All Filters
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SearchResultsPage;
